import { defineNuxtPlugin } from '#app'
import { useRoute } from 'vue-router'

interface AppcuesWindow extends Window {
  Appcues?: any;
  AppcuesSettings?: any;
}

interface UserResponse {
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    profile?: {
      timezone?: string;
    }
  } | null;
  role?: string;
  organization?: {
    id: number;
    name: string;
    slug: string;
    created_at: string;
    trial_ends_at: string | null;
    meta_data?: {
      default_timezone: string;
      plan: string;
      number_of_users: number;
      current_role: string;
      organization_category: string;
      board_size: number;
    }
  }
}

declare const window: AppcuesWindow

export default defineNuxtPlugin(async (nuxtApp) => {
  // Only run on client-side
  if (process.server) return

  const config = useRuntimeConfig()
  const route = useRoute()
  const appcuesId = config.public.appcuesId

  // Don't initialize if Appcues is not configured
  if (!appcuesId) {
    console.debug('Appcues is not configured, skipping initialization')
    return
  }

  // Initialize Appcues settings
  window.AppcuesSettings = { enableURLDetection: true }

  // Load Appcues script
  const script = document.createElement('script')
  script.src = `//fast.appcues.com/${appcuesId}.js`
  script.async = true
  document.head.appendChild(script)

  // Wait for script to load
  await new Promise((resolve) => {
    script.onload = resolve
  })

  // Initialize user data function
  const initializeAppcues = async () => {
    try {
      // Fetch auth data
      const { data } = await useFetch<UserResponse>(`/api/${route.params.org_slug}/auth`, {
        headers: {
          Accept: 'application/json',
          'X-Api-Request': 'true',
          'X-Include-OrgMeta': 'true'
        }
      })

      // Check if we have user data
      if (!data.value?.user) {
        console.debug('No authenticated user found')
        return
      }

      const userData = data.value.user

      // Identify user
      window.Appcues?.identify(userData.id, {
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        role: data.value.role || 'member',
        timezone: userData.profile?.timezone || '',
        org_slug: route.params.org_slug
      })

      // If org data with meta_data is present, make the group call
      if (data.value.organization?.meta_data) {
        const org = data.value.organization
        window.Appcues?.group(org.id, {
          org_id: org.id,
          organization: org.name,
          org_slug: org.slug,
          default_timezone: org.meta_data.default_timezone,
          plan: org.meta_data.plan,
          number_of_users: org.meta_data.number_of_users,
          created_at: org.created_at,
          trial_end_date: org.trial_ends_at,
          trial_form: {
            current_role: org.meta_data.current_role,
            organization_category: org.meta_data.organization_category,
            board_size: org.meta_data.board_size
          }
        })
      }
    } catch (error) {
      console.error('Failed to initialize Appcues:', error)
    }
  }

  // Initialize when plugin loads
  await initializeAppcues()
})